import React, {createContext, useContext, useState} from "react";
import {CommentProps, Setters, SettersDefault} from "./interfaces"


export const useCommentContext = () => {
    const context = useContext(CommentContext);
    if (!context) {
        throw new Error("useCommentContext must be used within a ContextProvider");
    }
    return context;
};

type CommentContextType = ({
    editState: boolean,
    isReplying: boolean;
    isCustomizing: boolean;
    isDeleted: boolean;
    set: Setters;
} & CommentProps);

export const CommentContext = createContext<CommentContextType | null>(null);


export const CommentContextProvider: React.FC<CommentProps & { children: React.ReactNode }> = ({ children, ...commentProperties }) => {
    const [isReplying, setIsReplying] = useState<boolean>(false);
    const toggleIsReplying = () : void => {setIsReplying(prevState => !prevState);}

    // For comments that the user does not own, useStates of comment content are superfluous
    let value: CommentContextType = {
            ...commentProperties,
            isReplying: isReplying,
            isCustomizing: false,
            editState: false,
            set: {...SettersDefault, replyToggle: toggleIsReplying},
    }

    // For comments that the user does own, we provide useStates to update values
    if ( commentProperties.isOwned || commentProperties.context.isAdmin ) {
        const {context} = commentProperties;
        const [messageState, setMessageState] = useState<string>(commentProperties.message);
        const [isCustomizing, setIsCustomizing] = useState<boolean>(false);
        const [editState, setEditState] = useState<boolean>(commentProperties.isEditable);
        const [isDeleted, setIsDeleted] = useState<boolean>(commentProperties.isDeleted);

        const toggleCustomizing = () : void => {setIsCustomizing(prevState => !prevState);};
        const toggleEditState = () : void => {setEditState(prevState => !prevState);};
        const setDeleted = () : void => {setIsDeleted(true)}

        value = {
            ...value,
            color: context.color,
            animal: context.animal,
            authorName: context.authorName,
            message: messageState,
            isCustomizing: isCustomizing,
            editState: editState,
            isDeleted: isDeleted,
            set : {
                color: context.setColor,
                animal: context.setAnimal,
                authorName: context.setAuthorName,
                message: setMessageState,
                isReplying: setIsReplying,
                replyToggle: toggleIsReplying,
                isCustomizing: setIsCustomizing,
                customizingToggle: toggleCustomizing,
                isDeleted: setIsDeleted,
                setDeleted: setDeleted,
                editState: setEditState,
                editToggle: toggleEditState,
            }
        }
    }

    // We return all context
    return (
        <CommentContext.Provider value={value}>
            {children}
        </CommentContext.Provider>
    )
}
